import { defineComponent as _defineComponent } from 'vue'
import {
	computed,
	onMounted,
	onUnmounted,
	ref,
	watch,
} from "vue";
import {
	convertToHumanAmount,
	convertToHumanNumber,
	convertToTokenAmount,
	ETradeOrderMethod,
	formatPrice,
	IToken,
	ITradeOrders,
	SOL_MINT,
	TradeOrdersDTO,
	USDC_MINT,
} from "@/shared";
import { useCacheStore, useWalletStore } from "@/pinia";
import {
	closeTradeOrder,
	createTradeOrders,
	indexTradeOrders,
} from "@/api/endpoints";
import Alert from "@/components/common/Alert.vue";
import TableResponsive from "@/components/common/TableResponsive.vue";
import { useAlertStore } from "@/pinia/alertStore";
import LimitOrderWarning from "@/components/common/LimitOrderWarning.vue";
import BotWalletNeededWarning from "@/components/common/BotWalletNeededWarning.vue";

// props
interface IProps {
	token: IToken;
	lastPrice?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LimitOrder',
  props: {
    token: null,
    lastPrice: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
	token: IToken;
	lastPrice?: number;
};



// computed
const currentOrderMethodIs = computed<(type: ETradeOrderMethod) => boolean>(
	() => (type) => {
		return method.value === type;
	}
);
const disableOrder = computed<boolean>(() => {
	if (!walletStore.isBotWallet) return true;
	if (underMaintenance.value) return true;
	if (loading.value) return true;
	if (priceIsInvalid.value) return true;
	if (amountIsInvalid.value) return true;
	if (method.value == ETradeOrderMethod.STOP_LOSS_TRAILING && delta.value == 0)
		return true;

	return false;
});
const priceIsInvalid = computed<boolean>(() => {
	return price.value <= 0;
});
const amountIsInvalid = computed<boolean>(() => {
	if (amount.value <= 0) return true;

	switch (amountType.value) {
		case "usd":
			if (amount.value < 1) return true;
			break;
		case "token":
			if (amount.value * price.value < 1) return true;
			break;
	}

	return false;
});
const amountInvalidReason = computed<string>(() => {
	if (amount.value <= 0) return "Should not be zero";

	switch (amountType.value) {
		case "usd":
			if (amount.value < 1) return "Min order value is $1";
			break;
		case "token":
			if (amount.value * price.value < 1) return "Min order value is $1";
			break;
		default:
			return `unknown amountType: ${amountType.value}`;
	}

	return "";
});
const currentTokenBalance = computed<number>(() => {
	const account = cacheStore.userTokenAccounts[props.token.mint];
	if (!account) return 0;

	return account.amount;
});
const isPriceEnabled = computed<(percent: number) => boolean>(
	() => (percent) => {
		const priceUSD = getLastUSDPrice.value;
		if (!priceUSD) return false;

		return price.value === priceUSD * percent;
	}
);
const isAmountByBalanceEnabled = computed<(percent: number) => boolean>(
	() => (percent) => {
		if (amount.value === 0) return false;
		return amount.value === currentTokenBalance.value * percent;
	}
);
const isAmountEnabled = computed<(value: number) => boolean>(() => (value) => {
	return amount.value === value;
});
const getLastUSDPrice = computed<number>(() => {
	if (props.lastPrice) return props.lastPrice;
	return cacheStore.getTokenPricesUSD(props.token?.mint)?.priceUSD ?? 0;
});
const currentPercent = computed<number>(() => {
	if (!props.lastPrice) return 0;
	return (
		100 *
		Math.abs(
			(price.value - props.lastPrice) / ((price.value + props.lastPrice) / 2)
		)
	);
});
const getFinalAmount = computed<number>(() => {
	if (amountType.value === "token") {
		return amount.value;
	}
	return amount.value / price.value;
});

// store
const cacheStore = useCacheStore();
const walletStore = useWalletStore();
const alertStore = useAlertStore();

// data
const orders = ref<ITradeOrders>([]);
const method = ref<ETradeOrderMethod>(ETradeOrderMethod.LIMIT);
const price = ref<number>(0);
const delta = ref<number>(0);
const amount = ref<number>(1);
const loading = ref<boolean>(false);
const interval = ref<number>(0);
const userChangedPrice = ref<boolean>(false);
const priceFocus = ref<boolean>(false);
const amountType = ref<"usd" | "token">("token");
const underMaintenance = ref<boolean>(false);

// methods
function init(): void {
	getOrders();
	startPool();
	setStartPrice();
}

function startPool(): void {
	interval.value = window.setInterval(getOrders, 10000);
}

function stopPool(): void {
	clearInterval(interval.value);
}

function setStartPrice(): void {
	_setPrice(getLastUSDPrice.value);
}

function _setPrice(_price: number): void {
	price.value = Number(_price.toPrecision(4));
}

async function getOrders(): Promise<void> {
	if (!walletStore.isConnected || !walletStore.isBotWallet) return;
	orders.value = TradeOrdersDTO(
		await indexTradeOrders(
			walletStore.address.toString(),
			props.token.mint
		).catch((e) => {
			if (orders.value.length > 0)
				alertStore.error("Failed to get order index", e);

			orders.value.length = 0;
		})
	);
}

async function deleteOrder(id: string): Promise<void> {
	loading.value = true;
	return closeTradeOrder(walletStore.address.toString(), id)
		.catch((e) => {
			const err = e.toString().replace("Error: ", "");
			alertStore.error(
				err.substring(0, 1).toUpperCase() + err.substring(1),
				"Failed to close order"
			);
		})
		.then((r) => {
			if (!r) return;

			alertStore.success("Order closed");
			getOrders();
		})
		.finally(() => {
			loading.value = false;
		});
}

async function submitOrder(): Promise<void> {
	loading.value = true;

	//If SOL we use USDC
	let srcMint = SOL_MINT;
	if (props.token.address.toString() === srcMint) {
		srcMint = USDC_MINT;
	}

	return createTradeOrders({
		wallet: walletStore.address.toString(),
		src: srcMint,
		dest: props.token.mint,
		method: method.value,
		price: price.value,
		inAmount: convertToTokenAmount({
			amount: getFinalAmount.value,
			decimals: props.token.decimals,
		}),
		delta: delta.value,
		expiry: 0, //TODO wire in
	})
		.catch((e) => {
			const err = e.toString().replace("Error: ", "");
			alertStore.error(
				err.substring(0, 1).toUpperCase() + err.substring(1),
				"Failed to create order"
			);
		})
		.then((r) => {
			if (!r) return;

			alertStore.success("Order placed");
			getOrders();
		})
		.finally(() => {
			loading.value = false;
		});
}

function setPrice(percent: number): void {
	const priceUSD = getLastUSDPrice.value;
	if (!priceUSD) return;

	userChangedPrice.value = false;
	_setPrice(priceUSD * percent);
}

function appendPrice(percent: number): void {
	userChangedPrice.value = true;
	_setPrice(price.value + price.value * percent);
}

function setDelta(d: number): void {
	delta.value = d;
}

function setAmountByBalance(percent: number): void {
	const tokenAmount =
		(currentTokenBalance.value / Math.pow(10, props.token.decimals)) * percent;

	if (amountType.value === "usd") {
		amount.value = tokenAmount * price.value;
		return;
	}

	amount.value = tokenAmount;
}

function setMethod(m: ETradeOrderMethod) {
	method.value = m;
}

// setup
onMounted(init);
onUnmounted(stopPool);

// watch
watch(
	() => getLastUSDPrice.value,
	() => {
		if (userChangedPrice.value || priceFocus.value) return;

		setStartPrice();
	}
);

// expose
expose({ orders, setMethod });

return { __sfc: true,props, currentOrderMethodIs, disableOrder, priceIsInvalid, amountIsInvalid, amountInvalidReason, currentTokenBalance, isPriceEnabled, isAmountByBalanceEnabled, isAmountEnabled, getLastUSDPrice, currentPercent, getFinalAmount, cacheStore, walletStore, alertStore, orders, method, price, delta, amount, loading, interval, userChangedPrice, priceFocus, amountType, underMaintenance, init, startPool, stopPool, setStartPrice, _setPrice, getOrders, deleteOrder, submitOrder, setPrice, appendPrice, setDelta, setAmountByBalance, setMethod, convertToHumanAmount, convertToHumanNumber, ETradeOrderMethod, formatPrice, Alert, TableResponsive, LimitOrderWarning, BotWalletNeededWarning }
}

})